import proj4 from "proj4";
import { CoordinateSystem, DEFAULT_EPSILON } from "./CoordinateSystem";
import { GridSamplingStrategyKind } from "./GridSamplingStrategy";
function isAreaFromMinMax(v) {
    return v.hasOwnProperty("latMin");
}
export class Area {
    constructor(crs, area) {
        this.crs = crs;
        if (isAreaFromMinMax(area)) {
            this.latMin = area.latMin;
            this.latMax = area.latMax;
            this.lngMin = area.lngMin;
            this.lngMax = area.lngMax;
        }
        else {
            this.latMin = area.south;
            this.latMax = area.north;
            this.lngMin = area.west;
            this.lngMax = area.east;
        }
    }
    get south() {
        return this.latMin;
    }
    set south(value) {
        this.latMin = value;
    }
    get north() {
        return this.latMax;
    }
    set north(value) {
        this.latMax = value;
    }
    get west() {
        return this.lngMin;
    }
    set west(value) {
        this.lngMin = value;
    }
    get east() {
        return this.lngMax;
    }
    set east(value) {
        this.lngMax = value;
    }
    width() {
        return this.lngMax - this.lngMin;
    }
    height() {
        return this.latMax - this.latMin;
    }
    lngExtent() {
        return this.width();
    }
    latExtent() {
        return this.height();
    }
    inCrs(v) {
        return this.crs === v;
    }
    project(toCrs) {
        if (this.inCrs(toCrs)) {
            return this.copy();
        }
        else {
            const [lngMin, latMin] = proj4(this.crs, toCrs, [this.lngMin, this.latMin]);
            const [lngMax, latMax] = proj4(this.crs, toCrs, [this.lngMax, this.latMax]);
            return new Area(toCrs, { latMin, latMax, lngMin, lngMax });
        }
    }
    static ofWeatherModel(weatherModel) {
        return this.ofWgs84BoundingBox(weatherModel.bounding_box);
    }
    static ofWgs84BoundingBox(bounding_box) {
        return new Area(CoordinateSystem.WGS84, bounding_box);
    }
    static fromString(boundingBox) {
        const coordinate = (name) => `(?<${name}>-?[0-9]+\\.[0-9]{16})`;
        const area = new RegExp(`^${coordinate("latMax")},${coordinate("lngMin")}_${coordinate("latMin")},${coordinate("lngMax")}$`);
        const matches = boundingBox.match(area)?.groups;
        return matches != null
            ? new Area(CoordinateSystem.WGS84, {
                latMax: +matches.latMax,
                lngMin: +matches.lngMin,
                latMin: +matches.latMin,
                lngMax: +matches.lngMax,
            })
            : null;
    }
    /**
     * Intersect two areas to yield a third area.
     *
     * Note, that the order `area1.intersect(area2.project(area1.crs))` matters if both areas are represented in different crs.
     * This is because `area2` has to be within the projectable region of `area1.crs`. The following example illustrates the issue:
     *
     * ```ts
     * const world_wgs84 = new Area(CoordinateSystem.WGS84, { latMin: -90, latMax: 90, lngMin: -180, lngMax: 180 });
     * const epsg3857_cutoff = 85.06;
     * const world_epsg3857 = new Area(CoordinateSystem.WGS84, { latMin: -epsg3857_cutoff, latMax: epsg3857_cutoff, lngMin: -180, lngMax: 180 });
     *
     * // errors since latitude of 90 in wgs84 cannot be projected onto epsg3857
     * world_epsg3857.intersect(world_wgs84.project(CoordinateSystem::EPSG3857));
     * // works since `epsg3857_cutoff` can be projected onto wgs84.
     * world_wgs84.intersect(world_epsg3857.project(CoordinateSystem::WGS84);
     * ```
     *
     * @return intersection or `null` if both areas do not overlap (area would be ill-defined since it is either negative or any zero area
     * point within the valid region of the projection)
     */
    intersect(other) {
        const area = new Area(this.crs, {
            latMax: Math.min(this.latMax, other.latMax),
            lngMax: Math.min(this.lngMax, other.lngMax),
            latMin: Math.max(this.latMin, other.latMin),
            lngMin: Math.max(this.lngMin, other.lngMin),
        });
        if (area.latMax < area.latMin || area.lngMax < area.lngMin) {
            return null;
        }
        return area;
    }
    /**
     * Check if two objects contain the same area in the same crs.
     * Returns `false` if `other` represents the same area in a different crs.
     */
    equals(other, eps = DEFAULT_EPSILON[this.crs]) {
        return (this === other ||
            (this.crs === other.crs &&
                Math.abs(this.latMax - other.latMax) <= eps &&
                Math.abs(this.lngMax - other.lngMax) <= eps &&
                Math.abs(this.latMin - other.latMin) <= eps &&
                Math.abs(this.lngMin - other.lngMin) <= eps));
    }
    /**
     * Check if this area contains the given area.
     *
     * @param smallerArea
     * @param eps disregards some smaller imprecisions. This can be helpful since reprojection roundtrips can
     * introduce small deltas.
     *
     * @returns
     */
    contains(smallerArea, eps = DEFAULT_EPSILON[this.crs]) {
        const intersection = this.intersect(smallerArea);
        if (intersection === null) {
            return { kind: "OutOfBounds" };
        }
        if (smallerArea.equals(intersection, eps)) {
            return { kind: "WithinBounds" };
        }
        return {
            kind: "PartiallyOutOfBounds",
            intersection,
        };
    }
    /**
     * Adapt the area to a grid sampling strategy.
     *
     * Effectively, this slightly shrinks the area to assign different meanings to grid points. See `GridSamplingStrategy` for details.
     * This operation is not invertible: `myArea.applySamplingStrategy(GridSamplingStrategy.Point) !== myArea.applySamplingStrategy(GridSamplingStrategy.Area).applySamplingStrategy(GridSamplingStrategy.Point)`
     */
    applySamplingStrategy(strategy, grid) {
        switch (strategy.kind) {
            case GridSamplingStrategyKind.Area:
                return this.applyAreaSamplingStrategy(grid, [0.5, 0.5]);
            case GridSamplingStrategyKind.AreaSample:
                return this.applyAreaSamplingStrategy(grid, strategy.sample);
            case GridSamplingStrategyKind.Point:
                return this.copy();
        }
    }
    /**
     * Compute the sample point of each cell and shrink the area to the new convex hull
     *
     * @param grid number of cells along each axis of the area
     * @param uv position of the sample within the cell
     *
     * @returns
     */
    applyAreaSamplingStrategy(grid, uv) {
        const cellHeight = this.height() / grid.height;
        const cellWidth = this.width() / grid.width;
        return new Area(this.crs, {
            latMin: this.latMin + cellHeight * uv[1],
            latMax: this.latMax - cellHeight * (1 - uv[1]),
            lngMin: this.lngMin + cellWidth * uv[0],
            lngMax: this.lngMax - cellWidth * (1 - uv[0]),
        });
    }
    copy() {
        return new Area(this.crs, this);
    }
    toTransferable() {
        return {
            crs: this.crs,
            latMin: this.latMin,
            latMax: this.latMax,
            lngMin: this.lngMin,
            lngMax: this.lngMax,
        };
    }
    roundToPrecision(precision) {
        return new Area(this.crs, {
            latMin: roundToPrecision(this.latMin, precision),
            latMax: roundToPrecision(this.latMax, precision),
            lngMin: roundToPrecision(this.lngMin, precision),
            lngMax: roundToPrecision(this.lngMax, precision),
        });
    }
    static fromTransferable(area) {
        return new Area(area.crs, area);
    }
}
function roundToPrecision(val, precision) {
    // TODO: this roundtrip through string is not necessarily performant
    // Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision)
    return +val.toFixed(precision);
}
//# sourceMappingURL=Area.js.map